import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  /** @type {Animation[]} */
  #panelAnimations = [];

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (this.#isAnimatedFacetPanel()) {
      event.preventDefault();
      this.#toggleAnimatedFacetPanel();
    }
  };

  #handleToggle = () => {
    if (this.#isAnimatedFacetPanel()) return;
    if (!this.details.open) return;
    if (this.dataset.closeSiblings !== 'true') return;

    const container = this.closest('scroll-hint, form') ?? this.parentElement;
    const siblings = container.querySelectorAll('accordion-custom[data-close-siblings="true"]');
    siblings.forEach((sibling) => {
      if (sibling !== this) {
        if (sibling instanceof AccordionCustom) {
          sibling.closeAnimatedPanel();
        } else {
          sibling.details.open = false;
        }
      }
    });
  };

  #isAnimatedFacetPanel() {
    return (
      this.details.matches('.facets__panel:not([data-auto-close-details])') || this.dataset.animatePanel === 'true'
    );
  }

  #getDetailsContent() {
    const content = this.details.querySelector('.details-content');

    return content instanceof HTMLElement ? content : null;
  }

  #cancelPanelAnimations() {
    for (const animation of this.#panelAnimations) {
      animation.cancel();
    }

    this.#panelAnimations = [];
  }

  #commitPanelAnimations() {
    for (const animation of this.#panelAnimations) {
      animation.commitStyles();
      animation.cancel();
    }
  }

  /**
   * @param {HTMLElement} content
   * @param {{ open: boolean }} state
   */
  #clearPanelAnimationStyles(content, { open }) {
    content.style.transition = 'none';

    if (open) {
      content.style.blockSize = 'auto';
      content.style.opacity = '1';
      content.style.removeProperty('overflow');
    } else {
      content.style.removeProperty('overflow');
      content.style.removeProperty('block-size');
      content.style.removeProperty('opacity');
    }

    void content.offsetHeight;
    content.style.removeProperty('transition');
  }

  #toggleAnimatedFacetPanel() {
    if (this.details.open) {
      this.closeAnimatedPanel();
      return;
    }

    this.#openAnimatedFacetPanel();
  }

  #openAnimatedFacetPanel() {
    if (this.dataset.closeSiblings === 'true') {
      const container = this.closest('scroll-hint, form') ?? this.parentElement;
      const siblings = container.querySelectorAll('accordion-custom[data-close-siblings="true"]');

      for (const sibling of siblings) {
        if (sibling !== this && sibling instanceof AccordionCustom) {
          sibling.closeAnimatedPanel();
        }
      }
    }

    const content = this.#getDetailsContent();
    if (!content) {
      this.details.open = true;
      return;
    }

    this.#cancelPanelAnimations();

    const { duration, easing } = getTransitionTiming(content);

    content.style.transition = 'none';
    content.style.overflow = 'clip';
    content.style.blockSize = '0px';
    content.style.opacity = '0';

    this.details.open = true;
    void content.offsetHeight;
    const targetHeight = content.scrollHeight;

    this.#panelAnimations = [
      content.animate(
        [
          { blockSize: '0px', opacity: 0 },
          { blockSize: `${targetHeight}px`, opacity: 1 }
        ],
        { duration, easing, fill: 'forwards' }
      )
    ];

    Promise.all(this.#panelAnimations.map((animation) => animation.finished)).then(() => {
      this.#commitPanelAnimations();
      this.#clearPanelAnimationStyles(content, { open: true });
      this.#panelAnimations = [];
    });
  }

  closeAnimatedPanel() {
    const content = this.#getDetailsContent();
    if (!this.details.open || !content) {
      this.details.open = false;
      return;
    }

    this.#cancelPanelAnimations();

    const { duration, easing } = getTransitionTiming(content);
    const startHeight = content.getBoundingClientRect().height;

    content.style.transition = 'none';
    content.style.overflow = 'clip';
    content.style.blockSize = `${startHeight}px`;
    content.style.opacity = '1';

    this.#panelAnimations = [
      content.animate(
        [
          { blockSize: `${startHeight}px`, opacity: 1 },
          { blockSize: '0px', opacity: 0 }
        ],
        { duration, easing, fill: 'forwards' }
      )
    ];

    Promise.all(this.#panelAnimations.map((animation) => animation.finished)).then(() => {
      this.#commitPanelAnimations();
      this.details.open = false;
      this.#clearPanelAnimationStyles(content, { open: false });
      this.#panelAnimations = [];
    });
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

const getTransitionTiming = (element) => {
  const styles = getComputedStyle(element);
  const durations = styles.transitionDuration.split(',').map((duration) => {
    const value = parseFloat(duration);

    if (Number.isNaN(value)) return 0;

    return duration.trim().endsWith('ms') ? value : value * 1000;
  });
  const duration = Math.max(...durations, 400) || 400;
  const easing = styles.getPropertyValue('--animation-easing').trim() || 'ease';

  return { duration, easing };
};

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
